import {
  Button,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { ReactNode, useEffect, useState } from 'react';
import { useLocation, useNavigate, matchPath } from 'react-router';
import { AUTURA_GRAYSCALE, getHighlightColorFromHex } from 'utils';
export const TAB_LAYOUT_HEADER_HEIGHT = 124;

export interface FormTab {
  label: string;
  route: string;
  beforeNavigate?: () => boolean;
  disabled?: boolean;
}
export interface FormTabNavigationProps {
  title: string;
  tabs: FormTab[];
  hidden?: boolean;
}

const FormTabNavigation = ({
  title,
  tabs,
  hidden,
}: FormTabNavigationProps): ReactNode => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [activeTab, setActiveTab] = useState<FormTab>(tabs[0]);
  const tabIsActive = (tab: FormTab) => {
    return activeTab?.label === tab.label;
  };

  useEffect(() => {
    const tab = tabs.find((tab) => {
      const match = matchPath(
        {
          path: `${tab.route}`,
          end: true,
          caseSensitive: false,
        },
        pathname,
      );
      return match;
    });
    if (tab) setActiveTab(tab);
  }, [pathname, tabs]);

  const handleClick =
    (tab: FormTab) => (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (tabIsActive(tab)) {
        return;
      }

      if (tab.beforeNavigate) {
        if (tab.beforeNavigate()) {
          setActiveTab(tab);
          navigate(tab.route, { replace: true });
        }
      } else {
        setActiveTab(tab);
        navigate(tab.route, { replace: true });
      }
    };

  const getButtonColor = (tab: FormTab, hover: boolean) => {
    if (tabIsActive(tab)) {
      return hover ? AUTURA_GRAYSCALE[100] : AUTURA_GRAYSCALE[200];
    }
    return hover
      ? getHighlightColorFromHex(theme.palette.primary.main)
      : 'transparent';
  };

  return hidden ? null : (
    <Stack
      direction={'column'}
      sx={{
        height: isMobile ? 'auto' : TAB_LAYOUT_HEADER_HEIGHT,
        flexShrink: 0,
        p: 2,
        display: 'flex',
      }}
    >
      <Typography
        variant={isMobile ? 'h3' : 'h1'}
        textTransform={'capitalize'}
        sx={{ pb: 1 }}
      >
        {title}
      </Typography>
      <Stack
        direction={'row'}
        gap={0.5}
        sx={{
          overflowX: isMobile ? 'auto' : 'visible',
          WebkitOverflowScrolling: 'touch',
          flexWrap: isMobile ? 'nowrap' : 'wrap',
          py: isMobile ? 1 : 0,
        }}
      >
        {tabs.map((tab) => (
          <Button
            key={tab.label}
            onClick={handleClick(tab)}
            variant='text'
            disabled={tab.disabled}
            sx={{
              borderRadius: '8px',
              px: 2,
              flexShrink: 0,
              backgroundColor: getButtonColor(tab, false),
              color: 'black',
              '&:hover': {
                backgroundColor: getButtonColor(tab, true),
              },
            }}
          >
            {tab.label}
          </Button>
        ))}
      </Stack>
    </Stack>
  );
};

export default FormTabNavigation;
